import { Scene } from '../Scene';
import { SceneManifest } from '../SceneManifest';
import { ImageCubeMapTexture } from '../../texture';
import { ProceduralSky, RotationSource, Sky, SkyBox } from '../../object';

export async function loadSkybox(scene: Scene, manifest: SceneManifest): Promise<void> {
  if (!manifest.skybox) return;

  const sky = await createSky(scene, manifest.skybox);
  if (sky) {
    scene.objectManager.addObject(sky);
  }
}

/**
 * Factory: build the concrete {@link Sky} implementation requested by the
 * manifest. Defaults to the image-based skybox when `type` is omitted, for
 * backwards compatibility with existing scene JSONs.
 */
async function createSky(
  scene: Scene,
  data: SceneManifest['skybox'] & {},
): Promise<Sky | undefined> {
  if (data.type === 'procedural') {
    const sky = new ProceduralSky(scene.ctx, scene);
    if (data.sunColor) sky.sunColor = data.sunColor as [number, number, number];
    if (data.sunIntensity !== undefined) sky.sunIntensity = data.sunIntensity;
    if (data.fogColor) sky.fogColor = data.fogColor as [number, number, number];
    if (data.horizonMist !== undefined) sky.horizonMist = data.horizonMist;
    if (data.exposure !== undefined) sky.exposure = data.exposure;
    if (data.sunDirection) sky.sunDirection = data.sunDirection as [number, number, number];
    if (data.showSun !== undefined) sky.showSun = data.showSun;
    if (data.showStars !== undefined) sky.showStars = data.showStars;
    if (data.showMoon !== undefined) sky.showMoon = data.showMoon;
    if (data.showClouds !== undefined) sky.showClouds = data.showClouds;
    if (data.cloudColor) sky.cloudColor = data.cloudColor as [number, number, number];
    if (data.cloudShadowColor)
      sky.cloudShadowColor = data.cloudShadowColor as [number, number, number];
    if (data.cloudCoverage !== undefined) sky.cloudCoverage = data.cloudCoverage;
    if (data.cloudDensity !== undefined) sky.cloudDensity = data.cloudDensity;
    if (data.cloudScale !== undefined) sky.cloudScale = data.cloudScale;
    if (data.cloudWind) sky.cloudWind = data.cloudWind as [number, number];
    return sky;
  }

  // Default: image-based cubemap (back-compat with existing scene JSONs).
  const cube = new ImageCubeMapTexture({ sourceDirectory: data.sourceDirectory });
  await cube.load(scene.ctx);
  const skybox = new SkyBox(
    scene.ctx,
    scene.ctx.engine.activeCamera as unknown as RotationSource,
  );
  skybox.cubeTexture = cube;
  return skybox;
}
